import type { editor } from 'monaco-editor';
import type { CardComponentList, ICardComponent } from '../Components/CodeEditor/Devices/DeviceCardBuilder';
import { buildHardwareCardsFromProjectCodeSync } from '../Components/CodeEditor/ConfigurationTabs/hardwareParser';
import { CardComponents } from '../Components/CodeEditor/Devices/DeviceCardBuilder';
import { initializeParameter } from '../Components/CodeEditor/Devices/DeviceCardBuilder';
import type { DeviceCardProps } from '../Components/CodeEditor/Devices/DeviceCardProps';
import type { FrameworkClass } from '../framework/framework.d';
import { FrameworkClasses } from '../framework/frameworkClasses';
import type { DriveUnitPortOccupancy } from '../redux/ApplicationSlice';
import { DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN } from './driveUnitPortSelection';
import { getDriveUnitPortInitEntries, parseDriveUnitPortValue, resolveDriveUnitPortFromVariableDeclaration } from './electronicsPortFromProgramCode';
import { resolveConstructorDeclarationLineIndex, splitCodeLines } from './pythonSourceScanUtils';
import { followerMotorPortElectronic, parseMechanismFollowerMotorsFromProjectCode } from './mechanismFollowerMotorUtils';

const VALID_HARDWARE_CARD_TYPES = new Set<string>(Object.keys(CardComponents));

const FRAMEWORK_CLASSES_WITH_CONSTRUCTOR_PORT = FrameworkClasses.filter((frameworkClass) => getDriveUnitPortInitEntries(frameworkClass).length > 0);

/** Framework class names that may appear in electronics/mechanism drag snippets with a port literal. */
export const DRIVE_UNIT_PORT_SNIPPET_CLASS_NAMES: readonly string[] = FRAMEWORK_CLASSES_WITH_CONSTRUCTOR_PORT.map(
  (frameworkClass) => frameworkClass.name
);

const DRIVE_UNIT_PORT_SNIPPET_CLASS_SET = new Set<string>(DRIVE_UNIT_PORT_SNIPPET_CLASS_NAMES);

export function isDriveUnitPortSnippetClassName(className: string): boolean {
  return DRIVE_UNIT_PORT_SNIPPET_CLASS_SET.has(className);
}

export function findFrameworkClassWithDriveUnitPort(className: string): FrameworkClass | undefined {
  if (!isDriveUnitPortSnippetClassName(className)) return undefined;
  return FrameworkClasses.find((frameworkClass) => frameworkClass.name === className);
}

function isHardwareCardType(uiPanel: string | null): uiPanel is CardComponentList {
  return uiPanel !== null && VALID_HARDWARE_CARD_TYPES.has(uiPanel);
}

/**
 * True when the card's variable declaration still exists in the current program text.
 *
 * Hardware cards lag Monaco by a parse cycle, so a card can still name a variable the user has just
 * renamed. Both port resolvers fall back to the framework default port when the declaration cannot
 * be found, which downstream reads as a genuine port change. Skipping unresolved cards keeps those
 * phantom ports out of the occupancy map until the next parse refreshes the cards.
 */
function cardDeclarationResolves(projectLines: string[], card: ICardComponent, frameworkClass: FrameworkClass): boolean {
  const parentName = card.props.parentName ?? '';
  if (!parentName.trim()) return true;

  const declarationLine = resolveConstructorDeclarationLineIndex(projectLines, parentName, {
    constructorClassName: frameworkClass.name,
    fallbackLineIndex0Based: Number.isFinite(card.props.lineNumber) ? card.props.lineNumber : undefined,
  });

  return declarationLine !== null;
}

/**
 * Which program text the occupancy must describe.
 *
 * `auto` reads the live editor model when one is reachable and falls back to `projectCode`.
 * `project-code` pins the result to the supplied `projectCode` even when an editor is available,
 * so callers whose cards were parsed from that snapshot cannot mix two different program texts.
 */
export interface BuildDriveUnitPortOccupancyOptions {
  programTextSource?: 'auto' | 'project-code';
}

/**
 * Derives drive-unit port occupancy from parsed hardware cards and the current editor model,
 * using the same `initializeParameter` path as `useDriveUnitPortRegistry`, so the Redux map
 * stays accurate even when Configure Hardware cards are not mounted.
 *
 * When no Monaco editor is available (e.g. dashboard), pass `projectCode` to resolve constructor
 * port literals from the loaded project's program text.
 */
/** Drive-unit port rows derived from program text when no live editor/registry state is available. */
export function buildDriveUnitPortOccupanciesFromProjectCode(projectCode: string): DriveUnitPortOccupancy[] {
  const code = projectCode.trim();
  if (!code) return [];

  return buildDriveUnitPortOccupanciesFromHardwareCards(buildHardwareCardsFromProjectCodeSync(code), undefined, code);
}

export function buildDriveUnitPortOccupanciesFromHardwareCards(
  cards: ICardComponent[],
  editor: editor.IStandaloneCodeEditor | undefined,
  projectCode?: string,
  options?: BuildDriveUnitPortOccupancyOptions
): DriveUnitPortOccupancy[] {
  const pinnedToProjectCode = options?.programTextSource === 'project-code';
  const resolvedEditor = pinnedToProjectCode ? undefined : editor ?? cards.find((c) => c.props.editor)?.props.editor;
  const model = resolvedEditor?.getModel?.();
  const canUseEditor = !!model && model.getLineCount() >= 1;
  const canUseProjectCode = !canUseEditor && !!projectCode?.trim();

  if (!canUseEditor && !canUseProjectCode) {
    return [];
  }

  const rows: DriveUnitPortOccupancy[] = [];
  const projectText = canUseEditor ? model!.getValue() : projectCode ?? '';
  const projectLines = splitCodeLines(projectText);

  for (const card of cards) {
    const frameworkClass = card.props.mechanismClass;
    if (!frameworkClass) continue;

    const portEntries = getDriveUnitPortInitEntries(frameworkClass);
    if (!portEntries.length) continue;

    // Only cards rendered in Configure Hardware contribute; uiPanel must map to a known card component.
    if (!isHardwareCardType(frameworkClass.uiPanel)) continue;

    if (!cardDeclarationResolves(projectLines, card, frameworkClass)) continue;

    for (const { parameter } of portEntries) {
      let port: number | null = null;
      if (canUseEditor) {
        const props: DeviceCardProps = {
          ...card.props,
          editor: resolvedEditor ?? card.props.editor,
          parameterName: parameter,
        };
        port = parseDriveUnitPortValue(initializeParameter(props));
      } else if (canUseProjectCode) {
        port = resolveDriveUnitPortFromVariableDeclaration(projectCode!, card.props.lineNumber, card.props.parentName, frameworkClass, parameter);
      }

      if (port === null || port < DRIVE_UNIT_PORT_MIN || port > DRIVE_UNIT_PORT_MAX) continue;
      rows.push({ driveUnitPort: port, electronic: `${card.props.id}:${parameter}` });
    }
  }

  return appendMechanismFollowerMotorPortRows(rows, cards, projectText);
}

/** Electronic keys (`cardId:parameter`) for drive-unit port parameters on parsed hardware cards. */
export function collectDriveUnitElectronicsFromHardwareCards(cards: ICardComponent[]): Set<string> {
  const electronics = new Set<string>();

  for (const card of cards) {
    const frameworkClass = card.props.mechanismClass;
    if (!frameworkClass || !isHardwareCardType(frameworkClass.uiPanel)) continue;

    for (const { parameter } of getDriveUnitPortInitEntries(frameworkClass)) {
      electronics.add(`${card.props.id}:${parameter}`);
    }
  }

  return electronics;
}

function appendMechanismFollowerMotorPortRows(
  rows: DriveUnitPortOccupancy[],
  cards: ICardComponent[],
  projectCode: string
): DriveUnitPortOccupancy[] {
  if (!projectCode.trim()) return rows;

  const followers = parseMechanismFollowerMotorsFromProjectCode(projectCode);
  if (!followers.length) return rows;

  const occupiedPorts = new Set(rows.map((row) => row.driveUnitPort));
  const next = [...rows];

  for (const card of cards) {
    const parentName = card.props.parentName;
    if (!parentName) continue;

    const cardFollowers = followers.filter((follower) => follower.mechanismVariableName === parentName);
    for (const follower of cardFollowers) {
      if (follower.port < DRIVE_UNIT_PORT_MIN || follower.port > DRIVE_UNIT_PORT_MAX) continue;
      if (occupiedPorts.has(follower.port)) continue;

      next.push({
        driveUnitPort: follower.port,
        electronic: followerMotorPortElectronic(card.props.id, follower.port),
      });
      occupiedPorts.add(follower.port);
    }
  }

  return next;
}
